import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { Icon } from '@iconify/react';
import type { PayrollImportRow, PeriodAnomaly } from 'src/types/payroll.types';
import { formatMoney } from 'src/services/payroll/calculatePay';
import type { PayrollMatchStatus } from 'src/services/payroll/parsePayrollCsv';

const STATUS_META: Record<
  PayrollMatchStatus,
  { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info' }
> = {
  matched: { label: 'Ready', color: 'success' },
  name_mismatch_warning: { label: 'Name warning', color: 'warning' },
  unmatched: { label: 'Unknown ID', color: 'error' },
  duplicate_in_file: { label: 'Duplicate', color: 'error' },
  invalid_grade: { label: 'Invalid grade', color: 'error' },
  already_paid: { label: 'Already paid', color: 'info' },
  zero_gross: { label: 'Zero gross', color: 'default' },
  inactive: { label: 'Inactive', color: 'error' },
  posted: { label: 'Posted', color: 'success' },
  post_failed: { label: 'Post failed', color: 'error' },
};

function periodSum(periods: Record<string, number> | Record<number, number>): number {
  return Object.values(periods || {}).reduce((s, v) => s + (Number(v) || 0), 0);
}

export function StatusChip({ status }: { status: PayrollMatchStatus }) {
  const meta = STATUS_META[status] || { label: status, color: 'default' as const };
  return (
    <Chip
      size="small"
      label={meta.label}
      color={meta.color}
      variant={meta.color === 'default' ? 'outlined' : 'filled'}
      sx={{ fontWeight: 600, borderRadius: 1.5 }}
    />
  );
}

interface AnomalyDialogProps {
  row: PayrollImportRow | null;
  onClose: () => void;
  onSave: (
    rowId: string,
    periodName: string,
    resolution: 'keep_both' | 'keep_first' | 'keep_second' | 'average'
  ) => void;
}

function AnomalyDialog({ row, onClose, onSave }: AnomalyDialogProps) {
  const [selectedResolutions, setSelectedResolutions] = useState<Record<string, string>>({});

  if (!row || !row.anomalies || row.anomalies.length === 0) return null;

  const handleResolutionChange = (periodName: string, value: string) => {
    setSelectedResolutions((prev) => ({ ...prev, [periodName]: value }));
  };

  const handleApply = () => {
    row.anomalies?.forEach((a) => {
      const chosen = (selectedResolutions[a.periodName] || a.resolution) as
        | 'keep_both'
        | 'keep_first'
        | 'keep_second'
        | 'average';
      onSave(row.id, a.periodName, chosen);
    });
    onClose();
  };

  return (
    <Dialog open={Boolean(row)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icon icon="mdi:alert-decagram-outline" width={24} color="#EDB42E" />
        <Typography variant="h6" fontWeight={700}>
          Period Conflict Resolution
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Scholar:{' '}
          <strong>
            {row.first_name} {row.last_name}
          </strong>{' '}
          (ID: <code>{row.external_student_id}</code>)
        </Typography>

        <Stack spacing={3}>
          {row.anomalies.map((a) => {
            const currentResolution = selectedResolutions[a.periodName] || a.resolution;
            const c1 = a.courses[0];
            const c2 = a.courses[1];
            const avg =
              a.courses.reduce((s, c) => s + c.progressPercent, 0) / (a.courses.length || 1);

            return (
              <Box
                key={a.periodName}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha('#EDB42E', 0.05),
                  border: '1px solid',
                  borderColor: alpha('#EDB42E', 0.25),
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Chip
                    size="small"
                    label={`Period ${a.periodName}`}
                    color="warning"
                    sx={{ fontWeight: 700 }}
                  />
                  <Typography variant="subtitle2" fontWeight={700}>
                    {a.courses.length} courses listed in this period
                  </Typography>
                </Stack>

                <Box sx={{ mb: 2, pl: 1 }}>
                  {a.courses.map((c, i) => (
                    <Typography key={i} variant="body2" sx={{ py: 0.25 }}>
                      • <strong>{c.courseName}</strong>: {c.progressPercent}%{' '}
                      {c.progressScore ? `(${c.progressScore})` : ''} → {formatMoney(c.progressPercent)}
                    </Typography>
                  ))}
                </Box>

                {a.isIdentical ? (
                  <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                    Both entries have identical scores ({c1?.progressPercent}%). They have been
                    automatically consolidated into a single period entry.
                  </Alert>
                ) : (
                  <FormControl component="fieldset">
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5 }}>
                      Choose resolution:
                    </Typography>
                    <RadioGroup
                      value={currentResolution}
                      onChange={(e) => handleResolutionChange(a.periodName, e.target.value)}
                    >
                      <FormControlLabel
                        value="keep_both"
                        control={<Radio size="small" />}
                        label={
                          <Typography variant="body2">
                            <strong>Keep Both (3A & 3B)</strong> — <em>Recommended</em> (Pays for both classes:{' '}
                            {formatMoney(c1?.progressPercent || 0)} + {formatMoney(c2?.progressPercent || 0)})
                          </Typography>
                        }
                      />
                      {c1 && (
                        <FormControlLabel
                          value="keep_first"
                          control={<Radio size="small" />}
                          label={
                            <Typography variant="body2">
                              Keep <strong>{c1.courseName}</strong> only ({formatMoney(c1.progressPercent)})
                            </Typography>
                          }
                        />
                      )}
                      {c2 && (
                        <FormControlLabel
                          value="keep_second"
                          control={<Radio size="small" />}
                          label={
                            <Typography variant="body2">
                              Keep <strong>{c2.courseName}</strong> only ({formatMoney(c2.progressPercent)})
                            </Typography>
                          }
                        />
                      )}
                      <FormControlLabel
                        value="average"
                        control={<Radio size="small" />}
                        label={
                          <Typography variant="body2">
                            <strong>Average grades</strong> ({avg.toFixed(2)}% → {formatMoney(avg)})
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: '#297041',
            '&:hover': { bgcolor: '#1f5833' },
          }}
        >
          Apply decision
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function PayrollRowsTable({
  rows,
  emptyMessage = 'No rows',
  dense,
  onResolveAnomaly,
}: {
  rows: PayrollImportRow[];
  emptyMessage?: string;
  dense?: boolean;
  onResolveAnomaly?: (
    rowId: string,
    periodName: string,
    resolution: 'keep_both' | 'keep_first' | 'keep_second' | 'average'
  ) => void;
}) {
  const [anomalyRow, setAnomalyRow] = useState<PayrollImportRow | null>(null);

  if (!rows.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer sx={{ maxHeight: 520, borderRadius: 2 }}>
        <Table stickyHeader size={dense ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              {['#', 'Student', 'ID', 'Periods Σ', 'Gross', 'Tax', 'Net', 'Status', 'Notes / Decisions'].map((h) => (
                <TableCell
                  key={h}
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha('#297041', 0.04),
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const note = row.error_message || (row.warnings?.[0] ?? '');
              const hasAnomalies = Boolean(row.anomalies && row.anomalies.length > 0);
              const courseSummary = row.courses
                ? row.courses.map((c) => `${c.periodKey}: ${c.courseName} (${c.progressPercent}%)`).join(' • ')
                : '';

              return (
                <TableRow
                  key={row.id || `${row.row_number}-${row.external_student_id}`}
                  hover
                  sx={{
                    bgcolor:
                      hasAnomalies
                        ? alpha('#EDB42E', 0.08)
                        : row.match_status === 'name_mismatch_warning'
                          ? alpha('#EDB42E', 0.06)
                          : row.match_status === 'posted'
                            ? alpha('#297041', 0.04)
                            : undefined,
                  }}
                >
                  <TableCell>{row.row_number}</TableCell>
                  <TableCell>
                    <Typography fontWeight={600} variant="body2">
                      {row.first_name} {row.last_name}
                    </Typography>
                    {row.grade_level && (
                      <Typography variant="caption" color="text.secondary">
                        Grade {row.grade_level}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {row.external_student_id || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {courseSummary ? (
                      <Tooltip title={courseSummary} arrow>
                        <Typography variant="body2" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                          {periodSum(row.periods as any)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      periodSum(row.periods as any)
                    )}
                  </TableCell>
                  <TableCell>{formatMoney(Number(row.gross))}</TableCell>
                  <TableCell>{formatMoney(Number(row.tax_withheld))}</TableCell>
                  <TableCell>
                    <Typography fontWeight={700} color="#297041">
                      {formatMoney(Number(row.net))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5} alignItems="flex-start">
                      <StatusChip status={row.match_status} />
                      {hasAnomalies && (
                        <Chip
                          size="small"
                          icon={<Icon icon="mdi:alert-outline" />}
                          label="Period Anomaly"
                          color="warning"
                          sx={{ fontWeight: 700, fontSize: '0.65rem' }}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ width: 360, maxWidth: 360, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    <Stack spacing={0.75} alignItems="flex-start">
                      {note ? (
                        <Tooltip title={note}>
                          <Typography
                            variant="caption"
                            color={row.error_message ? 'error.main' : 'warning.dark'}
                            sx={{
                              display: 'block',
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                              lineHeight: 1.45,
                            }}
                          >
                            {note}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}

                      {hasAnomalies && onResolveAnomaly && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<Icon icon="mdi:tune-vertical" />}
                          onClick={() => setAnomalyRow(row)}
                          sx={{
                            textTransform: 'none',
                            py: 0.25,
                            px: 1,
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            borderRadius: 1.5,
                          }}
                        >
                          Review decision ({row.anomalies?.length})
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <AnomalyDialog
        row={anomalyRow}
        onClose={() => setAnomalyRow(null)}
        onSave={(rowId, periodName, resolution) => {
          if (onResolveAnomaly) {
            onResolveAnomaly(rowId, periodName, resolution);
          }
        }}
      />
    </>
  );
}

export function isPayableStatus(status: PayrollMatchStatus): boolean {
  return status === 'matched' || status === 'name_mismatch_warning';
}

export function isProblemStatus(status: PayrollMatchStatus): boolean {
  return !isPayableStatus(status) && status !== 'posted';
}

export function RowLegend() {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
      {(
        [
          'matched',
          'name_mismatch_warning',
          'unmatched',
          'duplicate_in_file',
          'invalid_grade',
          'already_paid',
          'zero_gross',
        ] as PayrollMatchStatus[]
      ).map((s) => (
        <StatusChip key={s} status={s} />
      ))}
    </Stack>
  );
}
